import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { useState } from "react";
import { AsyncButton, Button, Checkbox, useAlert, WidgetWrapper } from "uxp/components";
import { extractContextData, generateFullUUID, hasValue, loadInitialData, tryParseJSON } from "../../../../util";
import { findDataTypeForAttributeMetaType, ISerializedUIField, IUIPropertyDefinitionMap, IWidgetTemplateInstanceProps, IWidgetTemplateProps, registerCardTemplate } from "../../../common/common";
import { FieldContainer } from "../../../common/FieldContainer";
import { generateIconProp } from "../../../common/icon-selector/IconEditor";
import { SelectableItem, selectUIProps } from "../../../common/SelectableItem";
import { ContainerProperty, extractContainerProperties } from "../../../common/ui-props/ContainerProperty";
import { extractIconProperties, IconProperty } from "../../../common/ui-props/IconProperty";
import { extractTextProperties, TextProperty } from "../../../common/ui-props/TextProperty";
import './form.scss'

const XPRProps: IUIPropertyDefinitionMap = {
    'container': ContainerProperty({
        background: {
            color: "#FFF",
            image: {
                image: "",
                position: "",
                size: "",
            },
        },
    }),
    'headerContainer': ContainerProperty({
        background: {
            color: "#5652C2",
            image: {
                image: "",
                position: "",
                size: "",
            },
        },
    }),

    'responseContainer': ContainerProperty({
        background: {
            color: "#FFF",
            image: {
                image: "",
                position: "",
                size: "",
            },
        },
    }),
    'responseText': TextProperty({
        text: 'Thank you for submitting this form',
        color: '#424242',
        size: '12px'
    }, 'Response'),
    'title': TextProperty({
        text: '',
        color: '#424242',
        size: '12px'
    }, 'Title'),
    'responseIcon': IconProperty({
        icon: 'checkCircle',
        color: '#424242',
        size: '40px'
    }),
    'action': TextProperty({
        text: '',
    }, 'Submit Action'),
};


const XPR: React.ForwardRefExoticComponent<React.RefAttributes<IWidgetTemplateInstanceProps> & IWidgetTemplateProps> = React.forwardRef((props, ref) => {

    let uiProps = props?.uiProps || {}
    const alerts = useAlert()
    const [loading, setLoading] = React.useState(false)
    let [contextData, setContextData] = React.useState<any>({})

    const [disableDrop, setDisableDrop] = useState(false)
    const [initialSource, setIntitialSource] = React.useState(props.initialSource);
    const [formMode, setFormMode] = React.useState('form');

    const containerRef = React.useRef(null)
    const actionTextRef = React.useRef<string>(null)

    let { styles: containerStyles } = extractContainerProperties(uiProps.container)
    let { styles: headerStyles } = extractContainerProperties(uiProps.headerContainer)
    let { styles: responseStyles } = extractContainerProperties(uiProps.responseContainer)
    let { icon, styles: iconStyles } = extractIconProperties(uiProps.responseIcon, 'checkCircle')
    if (!iconStyles.fontSize) iconStyles.fontSize = '40px';

    let { text: title, styles: titleStyles } = extractTextProperties(uiProps.title, 'Form')
    let { text: responseText, styles: responseTextStyles } = extractTextProperties(uiProps.responseText, 'Thank you for submitting this form')
    let actualFormMode = formMode;
    if (props.designer?.mode == "preview") {
        actualFormMode = props.uiProps?.formMode;
    }

    React.useImperativeHandle(ref, () => ({
        beforeSave: validateBeforeSave,
    }))


    React.useEffect(() => {
        loadInitialDataAndContextData()
    }, [props])

    async function loadInitialDataAndContextData() {
        const { isLoading, initialData } = await loadInitialData(props)
        setLoading(isLoading)

        const _data = await extractContextData(props, initialData, contextData)
        setContextData(_data)
    }

    React.useEffect(() => {

        console.log("fields -----------", uiProps);


        if (!hasValue(uiProps?.action?.text || '')) {
            if (!hasValue(actionTextRef)) {
                // update uiProps
                props.designer?.setUIProps(Object.assign({}, uiProps, { action: { text: actionTextRef.current } }))
            }
        }
    }, [uiProps])


    async function getModelName(modelKey: string) {
        try {
            let res = await props.uxpContext.executeService('System', 'MetadataMap:NameByKey', { Key: modelKey }, { json: true })
            if (res && res[0] && res[0].Name) return res[0].Name
            return { error: 'Unable to find the name for given model key' }
        } catch (e) {
            return { error: e }
        }
    }

    async function createDatabase(modelKey: string) {
        try {

            // get collections 
            let collections = await props.uxpContext.executeService('Lucy', 'MetadataMapCollection:GetAllCollectionsForModel', { MapKey: modelKey }, { json: true })
            console.log('collections ', collections);
            let dbName = '';
            let widgetName = props.designer?.widgetName;
            if (!!widgetName) {
                let inst = collections.find((c: any) => c.Name.trim() === widgetName.trim())
                if (!inst) {
                    dbName = widgetName;
                }
            }
            if (!dbName) {
                let data = await alerts.form({
                    title: 'Name your database',
                    content: 'We are going to create a database for you based on the fields in your form. You can modify this later if required',
                    formStructure: [
                        {
                            name: 'name',
                            label: 'Database Name',
                            type: 'string',
                            validate: {
                                customValidateFunction: (value: string) => {
                                    if (!hasValue(value)) return { valid: false, error: 'Database Name is required' }
                                    console.log(collections, 'collections');

                                    let inst = collections.find((c: any) => c.Name.trim() === value.trim())
                                    if (inst) {
                                        return { valid: false, error: 'Database with the given name exists. Please enter a different name' }
                                    }
                                    return { valid: true }
                                }
                            }
                        }
                    ],
                    submitButtonTitle: 'Done'
                })
                if (data == null) {
                    return { error: 'skipped' };
                }
                dbName = data.name;
            }

            // create form 
            const attributesForService = ((uiProps?.fields || []) as ISerializedUIField[]).filter(f => (f.isInputField)).map((f: ISerializedUIField) => {

                let metaType = f.metaType || 'text'
                return {
                    name: f.id,
                    dataType: findDataTypeForAttributeMetaType(metaType),
                    metaType: metaType
                }
            })

            const params = {
                'Name': dbName,
                'GUID': generateFullUUID(),
                'Attributes': JSON.stringify(attributesForService),
                'MapKey': modelKey
            }

            let dbCreateRes = await props.uxpContext.executeService('Lucy', 'MetadataMapCollection:Create', params, { json: true })
            if (dbCreateRes[0].Status === 'Created') return dbName;
            return { error: 'Unable to create the database. Invalid response' }
        }
        catch (e) {
            return { error: 'Unable to create the database. Exception: ' + e }
        }

    }

    async function validateBeforeSave(isNew: boolean, key?: string, modelKey?: string): Promise<boolean | { canSave: boolean, updatedProps: any }> {
        let hasActionConfigured = hasValue(uiProps?.action?.text || '')
        let hasModelKey = hasValue(modelKey)

        // check and create a database(collection) if the source is not set
        if (!hasActionConfigured) {

            if (hasValue(actionTextRef.current)) {
                return { canSave: true, updatedProps: Object.assign({}, uiProps, { action: { text: actionTextRef.current } }) }
            }

            // this can happen in the designer 
            // i think we should show an error here 
            if (!hasModelKey) {
                props.designer?.addErrors([
                    {
                        id: 'action',
                        containerId: 'container',
                        fieldId: 'text',
                        type: 'property',
                        error: 'Action is required'
                    }
                ])
                return false
            }
            else {
                // create the database and bind to action 
                // get model name 
                let name = await getModelName(modelKey)

                if (typeof name === 'object') {
                    alerts.show("Unable to find the project(model) for the given key. You need to set the action manually!")
                    props.designer?.addErrors([
                        {
                            id: 'action',
                            containerId: 'container',
                            fieldId: 'text',
                            type: 'property',
                            error: 'Action is required'
                        }
                    ])
                    return false
                }

                let collectionName = await createDatabase(modelKey)
                if (typeof collectionName === 'object') {
                    if (collectionName.error != 'skipped') {
                        alerts.show("Unable to create the database. You need to set the action manually!")
                        props.designer?.addErrors([
                            {
                                id: 'action',
                                containerId: 'container',
                                fieldId: 'text',
                                type: 'property',
                                error: 'Action is required'
                            }
                        ])
                    }
                    return false
                }

                // set action 
                const actionText: string = `collection:${name}.${collectionName}`

                // need to keep this value in a ref incase if something else fail 
                // since we are not updating the uiProps (here we are just passing to the saveWidget function)
                actionTextRef.current = actionText

                return { canSave: true, updatedProps: Object.assign({}, uiProps, { action: { text: actionText } }) }
            }
        }

        return true
    }





    React.useEffect(() => {
        let items = tryParseJSON(initialSource);
        if (!items) return;
        if (items.Type != 'collection' && items.Type != "external") return;
        let newFields: ISerializedUIField[] = [];
        let attributes = items.Attributes || [];

        const droppableContainerId = 'parent-form'
        for (let attr of attributes) {
            let { name, dataType, metaType } = attr;
            if (!metaType) metaType = '' + dataType;
            switch (metaType.toUpperCase()) {
                default:
                case 'PHONE':
                case 'TEXT':
                    {
                        newFields.push({
                            id: name,
                            name,
                            props: {
                                fieldLabel: {
                                    label: name
                                }
                            },
                            type: 'text',
                            isInputField: true,
                            droppableContainerId: droppableContainerId
                        });
                        break;
                    }
                case 'EMAIL':
                    {
                        newFields.push({
                            id: name,
                            name,
                            props: {
                                fieldLabel: {
                                    label: name
                                }
                            },
                            type: 'email',
                            isInputField: true,
                            droppableContainerId: droppableContainerId
                        });
                        break;
                    }
                case 'NAME':
                    {
                        newFields.push({
                            id: name,
                            name,
                            props: {
                                fieldLabel: {
                                    label: name
                                }
                            },
                            type: 'name',
                            isInputField: true,
                            droppableContainerId: droppableContainerId
                        });
                        break;
                    }
                case 'NUMBER':
                    {
                        newFields.push({
                            id: name,
                            name,
                            props: {
                                fieldLabel: {
                                    label: name
                                }
                            },
                            type: 'number',
                            isInputField: true,
                            droppableContainerId: droppableContainerId
                        });
                        break;
                    }
                case 'NOTES':
                    {
                        newFields.push({
                            id: name,
                            name,
                            props: {
                                fieldLabel: {
                                    label: name
                                }
                            },
                            type: 'textarea',
                            isInputField: true,
                            droppableContainerId: droppableContainerId
                        });
                        break;
                    }
                case 'POSITION':
                    {
                        newFields.push({
                            id: name,
                            name,
                            props: {
                                fieldLabel: {
                                    label: name
                                }
                            },
                            type: 'gps',
                            isInputField: true,
                            droppableContainerId: droppableContainerId
                        });
                        break;
                    }
                case 'DATE':
                    {
                        newFields.push({
                            id: name,
                            name,
                            props: {
                                fieldLabel: {
                                    label: name
                                }
                            },
                            type: 'date',
                            isInputField: true,
                            droppableContainerId: droppableContainerId
                        });
                        break;
                    }
                case 'TIME':
                    {
                        newFields.push({
                            id: name,
                            name,
                            props: {
                                fieldLabel: {
                                    label: name
                                }
                            },
                            type: 'time',
                            isInputField: true,
                            droppableContainerId: droppableContainerId
                        });
                        break;
                    }
                case 'TRUE/FALSE':
                    {
                        newFields.push({
                            id: name,
                            name,
                            props: {
                                fieldLabel: {
                                    label: name
                                }
                            },
                            type: 'checkbox',
                            isInputField: true,
                            droppableContainerId: droppableContainerId
                        });
                        break;
                    }
            }
        }

        let newProps = Object.assign({}, uiProps, { fields: newFields });
        newProps.title = { text: `${items.Action}`, color: '#FFF', size: '18px' };
        newProps.headerContainer = { background: { color: '#5652C2', image: { image: "", position: "", size: "", }, } };
        newProps.action = { text: `${items.Type}:${items.Name}.${items.Action}` };
        props.designer?.setUIProps(newProps);
        props.designer?.setWidgetProperties({
            name: items.Action,
            description: items.Action + ' Data Collection Form',
            height: 7 + newFields.length * 2,
        });

    }, []);

    React.useEffect(() => {
        updateHeight()
    }, [uiProps])

    React.useEffect(() => {

    }, [uiProps.fields])

    function updateHeight() {
        const container = containerRef.current as HTMLDivElement
        if (container) {
            const fieldContainer = container.querySelector('.body .droppable-area')
            if (props.updateWidgetHeight) {
                props.updateWidgetHeight(container.scrollHeight)
            }
        }
    }

    function prepareDataToSubmit() {
        // validate 
        let fields = uiProps?.fields || []
        let formData = contextData.form

        for (const f of fields) {
            if (f?.props?.fieldLabel?.required && !formData[f.id]) {

                // check number fields, skip validation (this is to handle zero (0))
                if (f.type === 'number' && hasValue(formData[f.id], true, f?.props?.allowNegativeValues)) continue

                const label = f?.props?.fieldLabel?.label || f.id
                alerts.show(`${label} is required`)
                return
            }
        }
        return formData
    }

    async function getModelKeyFormSource(source: string) {
        let _len = (source.startsWith('collection:')) ? 'collection:'.length : 'external:'.length

        let p = source.substring(_len);
        let [model, axn] = p.split('.');
        let keyData = await props.uxpContext.executeService('System', 'MetadataMap:KeyByName', { Name: model }, { json: true });
        let key = keyData?.[0]?.Key || '';
        if (!key) {
            throw 'Invalid key for ' + model;
        }

        return { modelKey: key, collectionName: axn }
    }

    async function getExternalDBId(modelKey: string, name: string) {
        let dbs = await props.uxpContext.executeComponent('ExternalDatabase', '/v1/databases', 'get', { model: modelKey })
        let db = dbs.find((d: any) => d.name == name)
        if (!db) {
            throw 'Unable to find the external db'
        }

        return db._id
    }

    async function submit() {
        if (props.designer?.mode == 'preview') {
            return;
        }
        if (props.designer?.mode == 'thumbnail') {
            return;
        }
        let action = '' + (props.uiProps?.action?.text || '');
        if (action.startsWith('collection:')) {

            // // validate 
            // let fields = uiProps?.fields || []
            // let formData = contextData.form

            // for (const f of fields) {
            //     if (f?.props?.fieldLabel?.required && !formData[f.id]) {

            //         // check number fields, skip validation (this is to handle zero (0))
            //         if (f.type === 'number' && hasValue(formData[f.id], true, f?.props?.allowNegativeValues)) continue

            //         const label = f?.props?.fieldLabel?.label || f.id
            //         alerts.show(`${label} is required`)
            //         return
            //     }
            // }


            let formData = prepareDataToSubmit()

            // append created user to formdata 
            formData['__createdUserKey__'] = props.uxpContext.userKey
            let { modelKey, collectionName } = await getModelKeyFormSource(action)
            await props.uxpContext.executeService('Lucy', 'AddNewDocument', { document: JSON.stringify(formData), collection: collectionName, model: modelKey, event: 1 }, { json: true });
            setFormMode('result');
        }
        else if (action.startsWith('external:')) {
            let formData = prepareDataToSubmit()
            let { modelKey, collectionName } = await getModelKeyFormSource(action)

            let dbId = await getExternalDBId(modelKey, collectionName)
            await props.uxpContext.executeComponent('ExternalDatabase', `/v1/database/${dbId}/data`, 'post', null, { values: formData })
            setFormMode('result')
        }
        else if (action.startsWith('action:')) {
            let formData = prepareDataToSubmit()
            const [type, model, maction] = action.split(":")

            await props.uxpContext.executeAction(model, maction, { ...formData }, { json: true })
            setFormMode('result')
        }
    }

    if (actualFormMode == 'result') {
        return <WidgetWrapper className={'uxp-form-template form-wrapper'}>
            <SelectableItem
                designer={props.designer}
                showAtRuntime={true}
                style={{ flex: 1 }}
                props={selectUIProps(uiProps, XPRProps, 'responseContainer')}
            >
                <div className='form-response' style={responseStyles}>

                    <div className="icon-container">
                        <SelectableItem
                            designer={props.designer}
                            className='icon-wrapper'
                            showAtRuntime={true}
                            props={selectUIProps(uiProps, XPRProps, 'responseIcon')}
                        >
                            <FontAwesomeIcon icon={generateIconProp(icon)} style={iconStyles} />
                        </SelectableItem>
                    </div>


                    <div className='message' style={responseTextStyles}>
                        <SelectableItem
                            designer={props.designer}
                            showAtRuntime={true}

                            style={{ padding: '20px' }}
                            props={selectUIProps(uiProps, XPRProps, 'responseText', 'responseTextStyles')}
                        >
                            {responseText || 'No response set'}

                            <div className="button-cont">
                                <Button title="Done" onClick={() => setFormMode('form')} />
                            </div>

                        </SelectableItem>
                    </div>

                </div>
            </SelectableItem>

        </WidgetWrapper>
    }
    return (<WidgetWrapper className={"uxp-form-template form-wrapper "}  >

        <SelectableItem
            designer={props.designer}
            props={selectUIProps(uiProps, XPRProps, 'container', 'action')}
            elementId='container'
        >
            <div className="container" ref={containerRef}>

                <div className="header">
                    <SelectableItem
                        designer={props.designer}
                        props={selectUIProps(uiProps, XPRProps, 'headerContainer', 'title')}
                    >
                        <div className="uxp-widget-title-bar uxp-drag-handler" style={headerStyles}>
                            <div className="title-section" style={titleStyles}>
                                <span className="title-icon"></span>
                                <span className="txt">{title}</span>
                            </div>
                            <div className="action-section">

                            </div>
                        </div>
                    </SelectableItem>
                </div>

                <div className="body" style={containerStyles}>
                    <FieldContainer
                        designer={props.designer}
                        uxpContext={props.uxpContext}
                        contextData={contextData}
                        onChangeData={setContextData}
                        uiProps={uiProps}
                        disabled={disableDrop}
                    />
                    <div className='form-widget-footer'>
                        <AsyncButton title="Submit" onClick={submit} />
                    </div>
                </div>


            </div>
        </SelectableItem >
    </WidgetWrapper >
    )
})

const TB: React.FunctionComponent<IWidgetTemplateProps> = props => {
    function changeMode(mode: string) {
        let newProps = Object.assign({}, props.uiProps, { formMode: mode });
        props.designer.setUIProps(newProps);
    }
    return <Checkbox checked={props.uiProps.formMode != 'result'} onChange={(b) => changeMode(b ? 'form' : 'result')} label={props.uiProps.formMode != 'result' ? 'Form' : 'Response'} type="switch-box" />
}
registerCardTemplate({
    id: 'form-widget',
    name: 'Form',
    description: 'A data entry form to collect information',
    template: XPR,
    toolbar: TB,
    canDragDropFields: true,
    icon: ['fas', 'credit-card-blank'],
    complexity: 'medium',
    autoHieght: true
});